import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { firestore } from "../firebase";

/**
 * Crea un registro de cambio de seriales en la tabla "registros"
 * @param {Object} params - Parámetros del registro
 * @param {string} params.tipoOperacion - Tipo específico de operación (ej: 'TRANSFERENCIA_REMITO', 'ELIMINACION_ORDEN_OT', etc.)
 * @param {string} params.componenteOrigen - Componente que ejecuta la operación (ej: 'AddRemito', 'ListOrdenes', etc.)
 * @param {string} params.idCuadrilla - ID de la cuadrilla afectada
 * @param {string} params.idRelacionado - ID relacionado (idRemito, idOrden, idCuadrilla, etc.)
 * @param {Array} params.serialesAgregados - Array de seriales agregados
 * @param {Array} params.serialesRemovidos - Array de seriales removidos
 * @param {Object} params.metadatos - Información adicional sobre la operación
 * @param {string} params.usuario - Usuario que realizó la operación (opcional)
 */
export const crearRegistroSeriales = async ({
    tipoOperacion,
    componenteOrigen,
    idCuadrilla,
    idRelacionado,
    serialesAgregados = [],
    serialesRemovidos = [],
    metadatos = {},
    usuario = null
}) => {
    try {
        const registroData = {
            tipoOperacion,
            componenteOrigen,
            idCuadrilla,
            idRelacionado,
            serialesAgregados,
            serialesRemovidos,
            cantidadAgregados: serialesAgregados.length,
            cantidadRemovidos: serialesRemovidos.length,
            metadatos,
            usuario,
            fecha: serverTimestamp(),
            timestamp: Date.now()
        };

        const docRef = await addDoc(collection(firestore, 'registros'), registroData);
        
        console.log('✅ Registro de seriales creado exitosamente:', docRef.id);
        console.log('📋 Detalles del registro:', {
            tipoOperacion,
            componenteOrigen,
            idCuadrilla,
            serialesAgregados: serialesAgregados.length,
            serialesRemovidos: serialesRemovidos.length
        });
        
        return docRef.id;
    } catch (error) {
        console.error('❌ Error al crear registro de seriales:', error);
        throw error;
    }
};

/**
 * Crea un registro específico para transferencias entre cuadrillas
 * @param {Object} params - Parámetros de la transferencia
 * @param {string} params.tipoOperacion - Tipo específico de operación (ej: 'TRANSFERENCIA_REMITO')
 * @param {string} params.componenteOrigen - Componente que ejecuta la operación (ej: 'AddRemito')
 * @param {string} params.idRemito - ID del remito
 * @param {string} params.idCuadrillaEmisora - ID de la cuadrilla que envía
 * @param {string} params.idCuadrillaReceptora - ID de la cuadrilla que recibe
 * @param {Array} params.serialesTransferidos - Array de seriales transferidos
 * @param {Array} params.serialesManuales - Array de seriales manuales agregados
 * @param {Object} params.metadatos - Información adicional
 */
export const registrarTransferenciaSeriales = async ({
    tipoOperacion,
    componenteOrigen,
    idRemito,
    idCuadrillaEmisora,
    idCuadrillaReceptora,
    serialesTransferidos = [],
    serialesManuales = [],
    metadatos = {}
}) => {
    try {
        const promesas = [];

        // Registro para la cuadrilla emisora (seriales removidos)
        if (serialesTransferidos.length > 0) {
            promesas.push(
                crearRegistroSeriales({
                    tipoOperacion,
                    componenteOrigen,
                    idCuadrilla: idCuadrillaEmisora,
                    idRelacionado: idRemito,
                    serialesAgregados: [],
                    serialesRemovidos: serialesTransferidos,
                    metadatos: {
                        ...metadatos,
                        cuadrillaDestino: idCuadrillaReceptora,
                        rol: 'EMISORA'
                    }
                })
            );
        }

        // Registro para la cuadrilla receptora (seriales agregados)
        const todosLosSeriales = [...serialesTransferidos, ...serialesManuales];
        if (todosLosSeriales.length > 0) {
            promesas.push(
                crearRegistroSeriales({
                    tipoOperacion,
                    componenteOrigen,
                    idCuadrilla: idCuadrillaReceptora,
                    idRelacionado: idRemito,
                    serialesAgregados: todosLosSeriales,
                    serialesRemovidos: [],
                    metadatos: {
                        ...metadatos,
                        cuadrillaOrigen: idCuadrillaEmisora,
                        serialesManuales: serialesManuales.length,
                        serialesTransferidos: serialesTransferidos.length,
                        rol: 'RECEPTORA'
                    }
                })
            );
        }

        const resultados = await Promise.all(promesas);
        console.log('✅ Registros de transferencia creados exitosamente:', resultados);
        
        return resultados;
    } catch (error) {
        console.error('❌ Error al registrar transferencia de seriales:', error);
        throw error;
    }
};

/**
 * Crea un registro para cuando se eliminan órdenes y se devuelven seriales
 * @param {Object} params - Parámetros del registro
 * @param {string} params.tipoOperacion - Tipo específico de operación (ej: 'ELIMINACION_ORDEN_OT')
 * @param {string} params.componenteOrigen - Componente que ejecuta la operación (ej: 'ListOrdenes')
 * @param {string} params.idOrden - ID de la orden eliminada
 * @param {string} params.idCuadrilla - ID de la cuadrilla
 * @param {Array} params.serialesDevueltos - Array de seriales devueltos
 * @param {Object} params.metadatos - Información adicional
 */
export const registrarEliminacionOrden = async ({
    tipoOperacion,
    componenteOrigen,
    idOrden,
    idCuadrilla,
    serialesDevueltos = [],
    metadatos = {}
}) => {
    return await crearRegistroSeriales({
        tipoOperacion,
        componenteOrigen,
        idCuadrilla,
        idRelacionado: idOrden,
        serialesAgregados: serialesDevueltos,
        serialesRemovidos: [],
        metadatos: {
            ...metadatos,
            accion: 'DEVOLUCION_SERIALES'
        }
    });
};

/**
 * Crea un registro para cuando se usan materiales y se consumen seriales
 * @param {Object} params - Parámetros del registro
 * @param {string} params.tipoOperacion - Tipo específico de operación (ej: 'USO_MATERIALES_ORDEN')
 * @param {string} params.componenteOrigen - Componente que ejecuta la operación (ej: 'AddMaterials')
 * @param {string} params.idOrden - ID de la orden relacionada
 * @param {string} params.idCuadrilla - ID de la cuadrilla
 * @param {Array} params.serialesUsados - Array de seriales usados
 * @param {Object} params.metadatos - Información adicional
 */
export const registrarUsoMateriales = async ({
    tipoOperacion,
    componenteOrigen,
    idOrden,
    idCuadrilla,
    serialesUsados = [],
    metadatos = {}
}) => {
    return await crearRegistroSeriales({
        tipoOperacion,
        componenteOrigen,
        idCuadrilla,
        idRelacionado: idOrden || `materiales_${Date.now()}`,
        serialesAgregados: [],
        serialesRemovidos: serialesUsados,
        metadatos: {
            ...metadatos,
            accion: 'CONSUMO_SERIALES'
        }
    });
};

/**
 * Crea un registro para actualizaciones directas de cuadrillas
 * @param {Object} params - Parámetros del registro
 * @param {string} params.tipoOperacion - Tipo específico de operación (ej: 'EDICION_CUADRILLA', 'ACTUALIZACION_MASIVA')
 * @param {string} params.componenteOrigen - Componente que ejecuta la operación (ej: 'EditCuadrilla', 'CuadrillasService')
 * @param {string} params.idCuadrilla - ID de la cuadrilla
 * @param {Array} params.serialesAnteriores - Array de seriales antes de la actualización
 * @param {Array} params.serialesNuevos - Array de seriales después de la actualización
 * @param {Object} params.metadatos - Información adicional
 */
export const registrarActualizacionDirecta = async ({
    tipoOperacion,
    componenteOrigen,
    idCuadrilla,
    serialesAnteriores = [],
    serialesNuevos = [],
    metadatos = {}
}) => {
    // Determinar qué seriales se agregaron y cuáles se removieron
    const serialesAgregados = serialesNuevos.filter(nuevo => 
        !serialesAnteriores.some(anterior => anterior.id === nuevo.id)
    );
    
    const serialesRemovidos = serialesAnteriores.filter(anterior => 
        !serialesNuevos.some(nuevo => nuevo.id === anterior.id)
    );

    return await crearRegistroSeriales({
        tipoOperacion,
        componenteOrigen,
        idCuadrilla,
        idRelacionado: `actualizacion_${Date.now()}`,
        serialesAgregados,
        serialesRemovidos,
        metadatos: {
            ...metadatos,
            totalAnteriores: serialesAnteriores.length,
            totalNuevos: serialesNuevos.length
        }
    });
}; 